import React, { useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { StyledSlider } from "@app/common/components/Slider.styles";
import styled from "styled-components";
import { resource } from "@app/common/localization/resource";

const sliderStyledProps = { height: 25, handleColor: "#00577C", trackHeight: 4 };

const currencyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

const startAmounts = [500, 1000, 2500, 5000, 7500, 10000, 25000, 50000, 75000, 100000, 250000, 500000, 750000, 1000000];
const monthlyAmounts = [100, 200, 300, 500, 750, 1000, 1500, 2000, 3000, 5000, 6000, 8000, 10000];

const WEALTH_RATE = 0.06;
const TRADITIONAL_RATE = 0.02;

const data = [
  {
    name: resource("landing.calculatorProductWealthElements"),
    areaColor: "#4986a0",
    markerColor: "#91C5CB"
  },
  {
    name: resource("landing.calculatorProductTraditional"),
    areaColor: "#86b7c3",
    markerColor: "#ffffff"
  }
];

export const InvestmentCalculator: React.VFC = () => {
  const [startAmount, setStartAmount] = useState(startAmounts[6]);
  const [monthlyAmount, setMonthlyAmount] = useState(monthlyAmounts[7]);
  const [selectedYear, setSelectedYear] = useState(25);

  const generateChartData = (
    startAmount: number,
    monthlyAmount: number,
    growthRate: number,
    selectedYear?: number
  ): Highcharts.SeriesAreaDataOptions[] => {
    const data: Highcharts.SeriesAreaDataOptions[] = [];
    let sumAmount: number;
    for (let i = 0; i <= 30; i++) {
      if (i === 0) {
        sumAmount = startAmount;
      } else {
        sumAmount = (sumAmount + monthlyAmount) * (1 + growthRate);
      }
      data.push({ x: i, y: sumAmount, selected: i === selectedYear });
    }
    return data;
  };

  /**
   * Change the max so visually when the amount is higher, it render a little bit higher
   */
  const getOffsetMaxValue = (startAmount: number, monthlyDepositAmount: number, growthRate: number): number => {
    const maxAmount = generateChartData(startAmount, monthlyDepositAmount, growthRate).pop().y;
    const singleOffset = 0.3 / (startAmounts.length - 1);
    const multiplier = startAmounts.length - 1 - startAmounts.indexOf(startAmount);
    return maxAmount / (1 - singleOffset * multiplier);
  };

  const handleStartAmount = (value: number) => {
    setStartAmount(startAmounts[value]);
  };

  const handleMonthlyAmount = (value: number) => {
    setMonthlyAmount(monthlyAmounts[value]);
  };

  const handleYear = (value: number) => {
    setSelectedYear(value);
  };

  const chartOptions: Highcharts.Options = {
    credits: {
      enabled: false
    },
    legend: {
      enabled: false
    },
    chart: {
      backgroundColor: "transparent",
      height: 360,
      margin: [10, 10, 10, 10]
    },
    title: {
      text: null
    },
    xAxis: {
      visible: false,
      minPadding: 0,
      maxPadding: 0
    },
    yAxis: {
      visible: false,
      endOnTick: false,
      startOnTick: false,
      max: getOffsetMaxValue(startAmount, monthlyAmount, WEALTH_RATE),
      min: startAmount
    },
    tooltip: {
      enabled: false
    },
    plotOptions: {
      series: {
        lineWidth: 0,
        animation: false,
        states: {
          normal: {
            animation: false
          },
          hover: {
            enabled: false,
            lineWidthPlus: 0
          },
          inactive: {
            opacity: 1
          }
        },
        marker: {
          enabled: true,
          fillColor: "transparent",
          lineColor: "transparent",
          lineWidth: 2,
          symbol: "circle",
          radius: 8
        }
      }
    },
    series: [
      {
        type: "area",
        name: data[0].name,
        data: generateChartData(startAmount, monthlyAmount, WEALTH_RATE, selectedYear),
        fillColor: {
          linearGradient: { x1: 0, x2: 1, y1: 0, y2: 0 },
          stops: [
            [0, "#ffffff"],
            [1, data[0].areaColor]
          ]
        },
        marker: {
          states: {
            select: {
              fillColor: data[0].markerColor,
              lineColor: "#00577C"
            }
          }
        }
      },
      {
        type: "area",
        name: data[1].name,
        data: generateChartData(startAmount, monthlyAmount, TRADITIONAL_RATE, selectedYear),
        fillColor: {
          linearGradient: { x1: 0, x2: 1, y1: 0, y2: 0 },
          stops: [
            [0, "#ffffff"],
            [1, data[1].areaColor]
          ]
        },
        marker: {
          states: {
            select: {
              fillColor: data[1].markerColor,
              lineColor: "#00577C"
            }
          }
        }
      }
    ]
  };

  return (
    <StyledInvestmentCalculator>
      <div className="options">
        <div className="sliders">
          <div className="slider">
            <div className="flex">
              <div>{resource("landing.calculatorStartingDeposit")}</div>
              <div>{currencyFormatter.format(startAmount)}</div>
            </div>
            <StyledSlider
              min={0}
              max={startAmounts.length - 1}
              onChange={handleStartAmount}
              value={startAmounts.indexOf(startAmount)}
              styledProps={sliderStyledProps}
            />
          </div>
          <div className="slider">
            <div className="flex">
              <div>{resource("landing.calculatorMonthlyDeposit")}</div>
              <div>{currencyFormatter.format(monthlyAmount)}</div>
            </div>
            <StyledSlider
              min={0}
              max={monthlyAmounts.length - 1}
              onChange={handleMonthlyAmount}
              value={monthlyAmounts.indexOf(monthlyAmount)}
              styledProps={sliderStyledProps}
            />
          </div>
          <div className="slider">
            <div className="flex">
              <div>{resource("landing.periodRequired")}</div>
              <div>
                {selectedYear} {resource("landing.calculatorYears")}
              </div>
            </div>
            <StyledSlider min={0} max={30} onChange={handleYear} value={selectedYear} styledProps={sliderStyledProps} />
            <div className="flex tips">
              <div>{resource("landing.calculatorNow")}</div>
              <div>30 {resource("landing.calculatorYears")}</div>
            </div>
          </div>
        </div>
        <div className="legends">
          <div className="header">{resource("landing.calculatorSeeYourResults")}</div>
          {chartOptions.series.map((s, i) => (
            <div key={i}>
              <div className="flex">
                <div>
                  <span className="marker" style={{ backgroundColor: data[i].markerColor }} />
                  <span>{data[i].name}: </span>
                </div>
                <div>
                  <span>{currencyFormatter.format(Math.round((s as any).data[selectedYear].y))}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="chart">
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        <div className="x-axis-labels">
          <div>{resource("landing.calculatorNow")}</div>
          <div>30 {resource("landing.calculatorYears")}</div>
        </div>
      </div>
    </StyledInvestmentCalculator>
  );
};

const StyledInvestmentCalculator = styled.div`
  display: grid;
  gap: 50px;
  grid-template-columns: 280px 780px;

  .options {
    font-size: 14px;
    font-weight: 600;
  }

  .chart {
    .x-axis-labels {
      display: flex;
      justify-content: space-between;
      margin: 5px 10px 0;
      font-size: 14px;
      font-weight: 600;
    }
  }

  .flex {
    display: flex;
    justify-content: space-between;
  }

  .sliders {
    .slider {
      margin: 0 0 25px;
    }
    .rc-slider-handle {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    }

    .tips {
      font-size: 12px;
    }
  }

  .legends {
    .header {
      margin: 50px 0 10px;
      font-size: 16px;
      font-weight: 700;
    }

    .flex {
      margin-bottom: 5px;
    }

    .marker {
      display: inline-block;
      width: 20px;
      height: 20px;
      border: 2px solid #00577c;
      border-radius: 99px;
      vertical-align: middle;
      margin: 0 5px 0.3em 0;
    }
  }
`;
